import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// FIX MED-001: Add animation styles to document head instead of using style jsx
const injectToastStyles = () => {
  const styleId = 'toast-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes toast-slide-in-right {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      .toast-animate-slide-in-right {
        animation: toast-slide-in-right 0.3s ease-out;
      }
    `;
    document.head.appendChild(style);
  }
};

/**
 * Toast Context for managing toast notifications
 */
const ToastContext = createContext(null);

/**
 * Toast Component - Individual toast notification
 */
const Toast = ({ id, type, message, onRemove }) => {
  const { t } = useTranslation('common');

  // FIX HIGH-001: Track mounted state to prevent race conditions
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Auto-dismiss after 5 seconds
    const timer = setTimeout(() => {
      // Only call onRemove if component is still mounted
      if (isMountedRef.current) {
        onRemove(id);
      }
    }, 5000);

    // Cleanup: mark as unmounted and clear timer
    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
    };
  }, [id, onRemove]);

  // Define toast styles based on type
  const typeStyles = {
    success: 'bg-green-600 text-white',
    error: 'bg-red-600 text-white',
    info: 'bg-blue-600 text-white',
    warning: 'bg-yellow-600 text-white'
  };

  // Define icons for each type
  const icons = {
    success: (
      <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
        <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
        <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
        <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    )
  };

  return (
    <div
      role="alert"
      className={`
        ${typeStyles[type]}
        min-w-[300px] max-w-md rounded-lg shadow-lg p-4 mb-3
        transform transition-all duration-300 ease-in-out
        toast-animate-slide-in-right hover:scale-105
        flex items-start gap-3
      `}
    >
      {/* Icon */}
      <div className="flex-shrink-0">
        {icons[type]}
      </div>

      {/* Message */}
      <div className="flex-1 text-sm font-medium">
        {message}
      </div>

      {/* Close Button */}
      <button
        onClick={() => onRemove(id)}
        className="flex-shrink-0 ml-2 hover:opacity-75 transition-opacity"
        aria-label={t('toast.ariaLabels.close')}
      >
        <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

Toast.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  type: PropTypes.oneOf(['success', 'error', 'info', 'warning']).isRequired,
  message: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired
};

/**
 * ToastProvider Component - Manages toast state and rendering
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // FIX MED-001: Inject animation styles on mount
  useEffect(() => {
    injectToastStyles();
  }, []);

  // Remove a toast by ID
  const removeToast = useCallback((id) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  // Add a new toast
  const addToast = useCallback((type, message) => {
    const id = Date.now() + Math.random(); // Ensure unique ID
    const newToast = { id, type, message };

    setToasts(prevToasts => [...prevToasts, newToast]);
  }, []);

  // Toast methods
  const toast = {
    success: (message) => addToast('success', message),
    error: (message) => addToast('error', message),
    info: (message) => addToast('info', message),
    warning: (message) => addToast('warning', message)
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}

      {/* Toast Container */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50">
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              id={toast.id}
              type={toast.type}
              message={toast.message}
              onRemove={removeToast}
            />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * useToast Hook - Access toast methods from any component
 */
export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
};

export default ToastProvider;